import React from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Button, Collapse, ListItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block', //これをしないとメニュー左側のアイコンが出てこない
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemGuide: {
    '& .itemGuide': {
      content: "''",
      display: 'block',
      position: 'absolute',
      zIndex: 1,
      top: 0,
      bottom: 0,
      borderLeft: '1px solid #e2e2e2'
    },
    '&:last-child': {
      '& > .itemGuide': {
        content: "''",
        display: 'block',
        position: 'absolute',
        zIndex: 1,
        top: 0,
        bottom: 21,
        borderLeft: '1px solid #e2e2e2'
      }
    }
  },
  button: {
    borderRadius: 0,
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.lightest
    }
  },
  buttonLeaf: {
    borderRadius: 0,
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        //&と$の間にスペースがないと効かない
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.lightest
    }
  },
  buttonGuide: {
    '& .buttonGuide': {
      content: "''",
      position: 'absolute',
      width: 5,
      height: 5,
      top: 20,
      backgroundColor: '#fff',
      border: '1px solid #e2e2e2',
      zIndex: 2
    }
  },
  activeButtonGuide: {
    '& .buttonGuide': {
      backgroundColor: theme.palette.primary.main
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  title: {
    marginRight: 'auto'
  },
  active: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.lightest,
    borderLeftColor: theme.palette.primary.main,
    borderLeftStyle: 'solid',
    borderLeftWidth: 4,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: theme.palette.primary.main
    }
  },
  collapse: {
    backgroundColor: theme.palette.primary.light
  }
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open,
  title,
  openNavItem,
  closeNavItem,
  ...rest
}) => {
  const classes = useStyles();

  const handleClick = () => {
    if (open) {
      closeNavItem(href);
    } else {
      openNavItem(href);
    }
  };

  let paddingLeft = 32;

  if (depth > 0) {
    paddingLeft = 32 + 40 * depth;
  }

  /*
    styleの中身は
    {paddingLeft: 8}
  */
  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className, {
          [classes.itemGuide]: depth > 0
        })}
        disableGutters
        key={title}
        {...rest}
      >
        <div className="itemGuide" style={{ left: paddingLeft - 30 }}></div>
        <Button
          className={clsx(classes.button, {
            [classes.buttonGuide]: depth > 0
          })}
          onClick={handleClick}
          style={style}
        >
          <div className="buttonGuide" style={{ left: paddingLeft - 32 }}></div>
          {Icon && <Icon className={classes.icon} size="20" />}
          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse className={clsx({ [classes.collapse]: open })} in={open}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className, {
        [classes.itemGuide]: depth > 0
      })}
      disableGutters
      key={title}
      {...rest}
    >
      <div className="itemGuide" style={{ left: paddingLeft - 30 }}></div>
      <Button
        activeClassName={clsx(classes.active, {
          [classes.activeButtonGuide]: depth > 0
        })}
        className={clsx(classes.buttonLeaf, `depth-${depth}`, {
          [classes.buttonGuide]: depth > 0
        })}
        component={RouterLink}
        style={style}
        to={href}
      >
        <div className="buttonGuide" style={{ left: paddingLeft - 32 }}></div>
        {Icon && <Icon className={classes.icon} size="20" />}
        <span className={classes.title}>{title}</span>
        {Info && <Info />}
      </Button>
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.node.isRequired
  ]),
  openNavItem: PropTypes.func,
  closeNavItem: PropTypes.func
};

NavItem.defaultProps = {
  open: false
};

export default NavItem;
